import { GridFilterModel, GridSortModel } from '@components/DataTable';
import {
  mediaCommentCountChangedEvent,
  mediaDeletedEvent,
  mediaUpdatedEvent,
} from '@lib/events/eventBus';
import {
  FOLDER_ROOT_PATH,
  FolderColorId,
  folderEntryPath,
  FolderPath,
  folderPathAncestry,
  folderPathDepth,
  folderPathName,
  isSameOrDescendantFolderPath,
  joinFolderPath,
  LibraryFolderEntry,
  parentFolderPath,
  repathFolderPath,
  resolveFolderPath,
} from '@lib/library/folderPath';
import {
  DEFAULT_FOLDER_SORT,
  FolderComparator,
  folderComparator,
  FolderSortFacts,
  FolderSortKey,
  isFolderSortKey,
} from '@lib/library/folderSort';
import { createContext } from '@lib/mobx/context';
import { DataFrame } from '@lib/mobx/dataframe';
import {
  createLibraryFolder,
  deleteLibraryFolder,
  LibraryFilesListing,
  listLibraryFiles,
  moveMediaToFolder,
  updateLibraryFolder,
} from '@lib/network/libraryFolders';
import { showToast } from '@lib/utils/toastmaster';
import { sortMedia } from '@pages/Library/utils/mediaSort';
import { isProbablyImage } from '@pages/Library/utils/mediaStatus';
import {
  folderMatchesOmniFilter,
  getOmniFields,
  matchesOmniFilter,
  parseOmniQuery,
} from '@pages/Library/utils/omniFilter';
import { billingStore } from '@stores/Billing';
import { omniFilterPreferences } from '@stores/OmniFilterPreferences';
import { trackingStore } from '@stores/Tracking';
import type { UploadDestination } from '@stores/UploadManager/UploadManager';
import { userStore } from '@stores/User';
import { Media } from '@typings/Media.js';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { VideoSelection } from '../MomentSelection/VideoSelection';
import { LibraryStore } from './Library.js';

const LIBRARY_SORT_MODEL_KEY = 'libraryMediaSortModel';
const FOLDER_SORT_KEY = 'libraryFolderSort';

// A per-viewer convenience: storage failing (a private window, blocked site data) means the
// default order, not an error.
const readStoredFolderSort = (): FolderSortKey => {
  try {
    const stored = localStorage.getItem(FOLDER_SORT_KEY);
    return isFolderSortKey(stored) ? stored : DEFAULT_FOLDER_SORT;
  } catch {
    return DEFAULT_FOLDER_SORT;
  }
};

interface RetryAllMediaResponse {
  success?: number;
  failed?: number;
  errors?: string[];
}

// Apparently, deleting doesn't immediately update the storage so we wait a bit.
// By a bit, we mean this number of milliseconds:
const CREDITS_REFRESH_WAIT_TIME_AFTER_DELETION_MS = 10_000;

// A product call: long enough that no real upload is still running, short enough that an abandoned
// one surfaces by the next day. Upload part URLs expire after 7 days, the hard upper bound.
const ABANDONED_UPLOAD_AGE_MS = 24 * 60 * 60 * 1000;

/** What a folder tile says about the folder: everything under it, not only its own media. */
export type FolderSummary = FolderSortFacts;

/** One row of the folder tree: a folder, or the library's root (the one row with no `entry`). */
export type FolderTreeRowData = { path: FolderPath; entry?: LibraryFolderEntry };

const NO_FOLDERS: readonly LibraryFolderEntry[] = [];
const NO_FOLDER_PATHS: ReadonlySet<FolderPath> = new Set();
const NO_FOLDERS_BY_PATH: ReadonlyMap<FolderPath, LibraryFolderEntry> = new Map();

/** The files listing without its folders, which `LibraryStore` holds. */
type LibraryFiles = Omit<LibraryFilesListing, 'folders'>;

/** The control that moved media. An "Undo" moves them too, tracked as `'undo'`. */
export type MediaMoveSource = 'menu' | 'bulk' | 'drag';

/** The control that moved a folder. An "Undo" moves it too, tracked as `'undo'`. */
export type FolderMoveSource = 'menu' | 'drag';

/** What a move changed: how many media moved, and the folder each of them left. */
export type MediaMove = { count: number; from: ReadonlyMap<FolderPath, string[]> };

/**
 * One library as the library page shows it: its folder tree, every media in it, and the folder
 * the user has open. The whole library is fetched once and folders are navigated client-side —
 * the listing is what the root view always loaded, and it lets a folder tile count what is under
 * it without a request per folder.
 */
export class LibraryFolderStore extends DataFrame<LibraryFiles, LibraryFilesListing> {
  readonly videoSelection: VideoSelection;

  /**
   * The folder on screen. Written only by `setCurrentFolderPath`, which the library page calls
   * from the URL's `?folder=`; everything that wants to change it navigates instead.
   */
  @observable
  currentFolderPath: FolderPath = FOLDER_ROOT_PATH;

  @observable
  showOnlyCompleted = false;

  @observable
  _sortModel: GridSortModel = [{ field: 'createdAt', sort: 'desc' }];

  // The library "omnifilter" query. Space-separated tokens; a media row is shown
  // if ANY token matches by title substring, exact YouTube ID, or exact media `_id`.
  // View-independent (drives both grid and table). See utils/omniFilter.ts.
  @observable
  _omniFilter: string | null = null;

  @observable
  _filterModel: GridFilterModel = { items: [] };

  /** How folders are ordered — in the row under the header and in the "All folders" tree alike. */
  @observable
  folderSort: FolderSortKey = readStoredFolderSort();

  @action
  setFolderSort(sort: FolderSortKey) {
    this.folderSort = sort;
    try {
      localStorage.setItem(FOLDER_SORT_KEY, sort);
    } catch {
      // Not remembered; the order still applies for this visit.
    }
  }

  @computed
  get compareFolders(): FolderComparator {
    return folderComparator(this.folderSort, (entry) => this.folderSummary(folderEntryPath(entry)));
  }

  @action
  setSortModel(sortModel: GridSortModel) {
    this._sortModel = sortModel;
    localStorage.setItem(LIBRARY_SORT_MODEL_KEY, JSON.stringify(sortModel));
  }

  @action
  setOmniFilter(query: string | null) {
    this._omniFilter = query?.trim() || null;
  }

  @computed
  get filterModel(): GridFilterModel {
    return this._filterModel;
  }

  @action
  setFilterModel(filterModel: GridFilterModel) {
    // The table's per-column filters only. The omnifilter is a separate,
    // view-independent query (`_omniFilter`) applied to the row data directly,
    // so it never lives in the DataTable filter model.
    this._filterModel = filterModel;
  }

  /**
   * `LibraryStore`'s tree, read through: empty until this listing is in, so no folder shows before
   * the media it holds. `folderPaths` and `foldersByPath` wait the same way.
   */
  @computed
  get folders(): readonly LibraryFolderEntry[] {
    return this.data ? this.library.folders : NO_FOLDERS;
  }

  @computed
  get folderPaths(): ReadonlySet<FolderPath> {
    return this.data ? this.library.folderPaths : NO_FOLDER_PATHS;
  }

  @computed
  get foldersByPath(): ReadonlyMap<FolderPath, LibraryFolderEntry> {
    return this.data ? this.library.foldersByPath : NO_FOLDERS_BY_PATH;
  }

  /** Whether `path` is a folder this library shows; the root always is. */
  hasFolder(path: FolderPath): boolean {
    return path === FOLDER_ROOT_PATH || this.folderPaths.has(path);
  }

  @computed
  private get childFoldersByParent(): ReadonlyMap<FolderPath, readonly LibraryFolderEntry[]> {
    const byParent = new Map<FolderPath, LibraryFolderEntry[]>();
    for (const folder of this.folders) {
      const siblings = byParent.get(folder.parent);
      if (siblings) siblings.push(folder);
      else byParent.set(folder.parent, [folder]);
    }
    for (const siblings of byParent.values()) siblings.sort(this.compareFolders);
    return byParent;
  }

  /** The folders directly inside `parent`, in `folderSort` order. */
  childFoldersOf(parent: FolderPath): readonly LibraryFolderEntry[] {
    return this.childFoldersByParent.get(parent) ?? NO_FOLDERS;
  }

  /**
   * The library's tree depth-first, each folder followed by what is inside it, starting at the root
   * (the one row with no `entry`). Each folder's subfolders come in `folderSort` order — the one
   * the row under the header uses — so the tree keeps its shape while the order changes.
   */
  @computed
  get folderTree(): readonly FolderTreeRowData[] {
    const rows: FolderTreeRowData[] = [{ path: FOLDER_ROOT_PATH }];
    const visit = (parent: FolderPath) => {
      for (const entry of this.childFoldersOf(parent)) {
        const path = folderEntryPath(entry);
        rows.push({ path, entry });
        visit(path);
      }
    };
    visit(FOLDER_ROOT_PATH);
    return rows;
  }

  @computed
  get childFolders(): readonly LibraryFolderEntry[] {
    return this.childFoldersOf(this.currentFolderPath);
  }

  /** The open folder's subfolders the omnifilter lets through — what the folder row shows. */
  @computed
  get visibleChildFolders(): LibraryFolderEntry[] {
    return this.childFolders.filter((entry) =>
      folderMatchesOmniFilter(entry.name, this._omniFilter, omniFilterPreferences.matchAll),
    );
  }

  /** Every media in the library, whichever folder holds it. */
  @computed
  get allFiles(): Media[] {
    return this.data?.files ?? [];
  }

  /** The folder `media` shows in. See `resolveFolderPath` for media whose folder is gone. */
  folderOf(media: Media): FolderPath {
    return resolveFolderPath(media.folderPath, this.folderPaths);
  }

  @computed
  private get filesInCurrentFolder(): Media[] {
    return this.allFiles.filter((file) => this.folderOf(file) === this.currentFolderPath);
  }

  /**
   * The media the views list before the omnifilter narrows them: the open folder's, or — while
   * there is a query — everything under it too. A search from the top of a library filed into
   * folders would otherwise find nothing. `MediaFolderLocation` says where a match from below
   * lives.
   */
  @computed
  private get filesInView(): Media[] {
    if (!this._omniFilter) return this.filesInCurrentFolder;
    return this.allFiles.filter((file) =>
      isSameOrDescendantFolderPath(this.folderOf(file), this.currentFolderPath),
    );
  }

  @computed
  get folderSummaries(): ReadonlyMap<FolderPath, FolderSummary> {
    const summaries = new Map<FolderPath, FolderSummary>();
    const summaryOf = (path: FolderPath) => {
      let summary = summaries.get(path);
      if (!summary) summaries.set(path, (summary = { mediaCount: 0 }));
      return summary;
    };
    for (const file of this.allFiles) {
      if (file.status === 'empty') continue;
      const changedAt = file.updatedAt ?? file.createdAt;
      for (const path of folderPathAncestry(this.folderOf(file))) {
        const summary = summaryOf(path);
        summary.mediaCount++;
        if (changedAt > (summary.lastModifiedAt ?? 0)) summary.lastModifiedAt = changedAt;
      }
    }
    return summaries;
  }

  folderSummary(path: FolderPath): FolderSummary {
    return this.folderSummaries.get(path) ?? { mediaCount: 0 };
  }

  /** Whether an upload lands in the folder on screen. */
  holdsUpload(destination: UploadDestination): boolean {
    return (
      destination.libraryId === this.library.libraryId &&
      destination.folderPath === this.currentFolderPath
    );
  }

  /**
   * Whether the view is the whole library, so the library's own stats describe it. False inside a
   * folder, and at the root once any media is filed elsewhere.
   */
  @computed
  get showsWholeLibrary(): boolean {
    return this.filesInCurrentFolder.length === this.allFiles.length;
  }

  /** Whether the view is a library with no media at all, rather than one empty folder of it. */
  @computed
  get showsEmptyLibrary(): boolean {
    return this.library.stats?.count === 0 && this.currentFolderPath === FOLDER_ROOT_PATH;
  }

  /**
   * Whether the open folder has subfolders and no media of its own, so its folders are what the
   * page is about. Media still `empty` don't count: no view lists them. False until the listing
   * has answered — a failed fetch says nothing about what the folder holds.
   */
  @computed
  get holdsOnlyFolders(): boolean {
    return (
      this.data !== null &&
      this.childFolders.length > 0 &&
      this.filesInCurrentFolder.every((file) => file.status === 'empty')
    );
  }

  @computed
  get files() {
    let filesToSort = this.filesInView.slice();

    const parsed = parseOmniQuery(this._omniFilter, omniFilterPreferences.matchAll);
    if (parsed) {
      filesToSort = filesToSort.filter((file) => matchesOmniFilter(getOmniFields(file), parsed));
    }

    return sortMedia(filesToSort, this.effectiveSortModel);
  }

  /**
   * `filesInView` sorted by current sort model, WITHOUT the omnifilter. Used by DataTable
   * so it can apply its per-column filterModel client-side. The omnifilter is applied to
   * the table's rows separately in `useMediaData` (keeps DataTable's filtered-row stats correct).
   */
  @computed
  get filesForTable() {
    return sortMedia(this.filesInView.slice(), this.effectiveSortModel);
  }

  @computed
  get filesCount() {
    return this.files.length;
  }

  @computed
  get filesCountCompleted() {
    return this.files.filter((file) => file.status === 'completed').length;
  }

  @computed
  get filesCountIndexing() {
    return this.files.filter((file) => file.status === 'processing').length;
  }

  @computed
  get filesCountQueued() {
    return this.files.filter((file) => file.status === 'queued').length;
  }

  @computed
  get filesCountProcessing() {
    return this.filesCountIndexing + this.filesCountQueued;
  }

  @computed
  get filesCountEmpty() {
    return this.files.filter((file) => file.status === 'empty').length;
  }

  @computed
  get filesCountFailed() {
    return this.files.filter((file) => file.status === 'errored').length;
  }

  // The four lists below span the whole library, not the open folder: they back the header's
  // "processing" and "failed" lines and the failed-media modal, whose retry and delete act on the
  // whole library — a failure filed in a subfolder must not disappear from them at the root.

  @computed
  get filesQueued() {
    return this.allFiles.filter((file) => file.status === 'queued');
  }

  @computed
  get filesUploading() {
    return this.allFiles.filter((file) => file.status === 'uploading');
  }

  @computed
  get filesIndexing() {
    return this.allFiles.filter((file) => file.status === 'processing');
  }

  @computed
  get filesFailed() {
    return this.allFiles.filter((file) => file.status === 'errored');
  }

  /**
   * Uploads that were started and never finished: still `empty` a day after
   * creation. Part PUTs go straight to storage and never touch the media, so a
   * live upload in another tab looks identical to an abandoned one — the age is
   * the only thing telling them apart. Recomputed when the files change, not as
   * time passes.
   *
   * FIXME: the age cutoff stands in for a server-side sweep of abandoned `empty` media, which
   * could tell a live upload from a dead one by its parts' last activity. Remove this list once
   * that sweep ships (see `.claude/docs/upload-failure-modes.md`, "Still open").
   */
  @computed
  get filesAbandonedUploads() {
    const cutoff = Date.now() - ABANDONED_UPLOAD_AGE_MS;
    return this.allFiles.filter((file) => file.status === 'empty' && file.createdAt < cutoff);
  }

  /** The media the Failed media modal lists and "Clear all" deletes. */
  @computed
  get filesFailedOrAbandoned() {
    return [...this.filesFailed, ...this.filesAbandonedUploads];
  }

  @computed
  get filesCountDisplay() {
    return this.filesCountCompleted + this.filesCountProcessing + this.filesCountFailed;
  }

  @computed
  get filesFilteredDuration() {
    // Calculate total duration from filtered files (applies the omnifilter if active)
    return this.files.reduce(
      (acc, file) => acc + (isProbablyImage(file) ? 0 : file.metadata?.video?.duration || 0),
      0,
    );
  }

  @computed
  get filesFilteredSize() {
    return this.files.reduce(
      (acc, file) => acc + (file.metadata?.video?.originalMediaSize || 0),
      0,
    );
  }

  @computed
  get hasPublishedAtMedia() {
    return this.allFiles.some((file) => file.publishedAt != null);
  }

  @computed
  get effectiveSortModel(): GridSortModel {
    const sort = this._sortModel;
    if (sort.length > 0 && sort[0].field === 'publishedAt' && !this.hasPublishedAtMedia) {
      // No media has publishedAt data (e.g. non-YouTube library) — silently
      // fall back to createdAt with the same direction so the UI doesn't break.
      return [{ field: 'createdAt', sort: sort[0].sort }];
    }
    return sort;
  }

  @computed
  get uniqueIds() {
    const uniqueIdsDict: Record<string, string> = {};

    this.allFiles.forEach((media) => {
      if (media.uniqueId && media.status !== 'empty') {
        uniqueIdsDict[media.uniqueId] = media._id;
      }
    });

    return uniqueIdsDict;
  }

  constructor(readonly library: LibraryStore) {
    super({ autoFetch: 'nextTick' });
    this.library = library;
    this.videoSelection = new VideoSelection(this);

    // Initialize sort model from localStorage
    const storedSortModel = localStorage.getItem(LIBRARY_SORT_MODEL_KEY);
    if (storedSortModel) {
      try {
        this._sortModel = JSON.parse(storedSortModel);
      } catch {
        // Keep default sort model
      }
    }

    makeObservable(this);

    // Add reaction to library data changes
    this.reaction(
      () => this.library.data,
      () => this.update(),
    );

    // Clear video selection when folder changes
    this.reaction(
      () => this.currentFolderPath,
      () => this.videoSelection.clearSelection(),
    );

    // Drop media the UploadManager just deleted (e.g. cancel-batch from the
    // upload status panel, which has no folder context and can't call into
    // this store directly). Unsubscribe is piggy-backed onto the
    // ReactionManager's disposer list so it fires on deinit.
    this._reactions.push(
      mediaDeletedEvent.on(({ libraryId, mediaIds }) => {
        if (libraryId !== this.library.libraryId) return;
        if (!this.data || mediaIds.length === 0) return;
        const toPrune = new Set(mediaIds);
        runInAction(() => {
          this.data!.files = this.data!.files.filter((f) => !toPrune.has(f._id));
        });
      }),
    );

    // FIXME: a listing sent before a comment write and answered after it (this store's own
    // fetch, or a MediaStatusPoller sweep through `mediaUpdatedEvent`) puts the old count back.
    // Acceptable once: the next listing that carries the media corrects it. Retire this when a
    // listing can tell that it predates a local write.
    this._reactions.push(
      mediaCommentCountChangedEvent.on(async ({ libraryId, mediaId, delta }) => {
        if (libraryId !== this.library.libraryId) return;
        const cached = this.data?.files.find((f) => f._id === mediaId)?.commentCount;
        if (cached === undefined) return;
        let next = cached + delta;
        // Below zero, the listing predates a comment made elsewhere (another tab, another
        // person), so re-read the count instead of showing a negative one.
        if (next < 0) {
          try {
            const media = await userStore.apiWithAuth<Media>(
              `/library/${libraryId}/media/${mediaId}`,
            );
            if (media.commentCount === undefined) return;
            next = media.commentCount;
          } catch (error) {
            console.error('Failed to re-read comment count:', error);
            return;
          }
        }
        runInAction(() => {
          if (!this.data) return;
          this.data.files = this.data.files.map((f) =>
            f._id === mediaId ? { ...f, commentCount: next } : f,
          );
        });
      }),
    );

    // Patch local files when another store (e.g. Notifications polling) has
    // fresh Media records. Only patches entries already present in `data.files`
    // — does not add new ones; the folder endpoint remains the source of truth
    // for which media belong here.
    this._reactions.push(
      mediaUpdatedEvent.on(({ libraryId, media }) => {
        if (libraryId !== this.library.libraryId) return;
        if (!this.data || media.length === 0) return;
        const byId = new Map(media.map((m) => [m._id, m]));
        const existingIds = new Set(this.data.files.map((f) => f._id));
        const hasMatch = media.some((m) => existingIds.has(m._id));
        if (!hasMatch) return;
        runInAction(() => {
          this.data!.files = this.data!.files.map((f) => byId.get(f._id) ?? f);
        });
      }),
    );

    // @ts-ignore
    window.folder = this;
  }

  protected async fetch(abortSignal: AbortSignal): Promise<LibraryFilesListing> {
    const listing = await listLibraryFiles(this.library.libraryId, abortSignal);
    const known = new Set(listing.folders.map(folderEntryPath));
    const orphaned = listing.files.filter(
      (file) => file.folderPath && resolveFolderPath(file.folderPath, known) !== file.folderPath,
    );
    if (orphaned.length > 0) {
      // Only an interrupted folder delete leaves these (see `resolveFolderPath`); they are shown
      // in the nearest surviving folder, and deleting them from there finishes the cascade.
      console.warn(
        `Library ${this.library.libraryId} holds ${orphaned.length} media filed in folders it no longer has`,
        orphaned.map((file) => ({ mediaId: file._id, folderPath: file.folderPath })),
      );
    }
    return listing;
  }

  /** Runs only for the listing that answers the latest fetch, so an older tree never lands. */
  protected override postProcess({ folders, ...files }: LibraryFilesListing): LibraryFiles {
    this.library.setFolders(folders);
    return files;
  }

  @action
  setCurrentFolderPath(path: FolderPath) {
    this.currentFolderPath = path;
  }

  /** Creates a folder and returns its path. */
  async createFolder(name: string, parent: FolderPath, color?: FolderColorId) {
    const path = joinFolderPath(parent, name);
    if (!path) throw new Error(`"${name}" is not a folder name`);
    await createLibraryFolder(this.library.libraryId, { parent, name: name.trim(), color });
    trackingStore.folderCreated({
      libraryId: this.library.libraryId,
      depth: folderPathDepth(path),
    });
    return path;
  }

  /**
   * Renames, moves or recolours a folder, in one write, and returns its path afterwards. The
   * media under it are repathed here rather than refetched: the backend moves them in the same
   * transaction as the tree, so the answer to this call is the answer for them too.
   */
  async updateFolder(
    path: FolderPath,
    updates: { name?: string; parent?: FolderPath; color?: FolderColorId | null },
    /** Which control moved it, when `parent` changes — for tracking. */
    via: FolderMoveSource | 'undo' = 'menu',
  ) {
    const name = updates.name?.trim();
    const newPath = joinFolderPath(
      updates.parent ?? parentFolderPath(path),
      name ?? folderPathName(path),
    );
    if (!newPath) throw new Error(`Cannot update ${path} with ${JSON.stringify(updates)}`);

    await updateLibraryFolder(this.library.libraryId, path, { ...updates, name });

    if (newPath !== path) {
      runInAction(() => {
        if (!this.data) return;
        this.data.files = this.data.files.map((file) =>
          file.folderPath && isSameOrDescendantFolderPath(file.folderPath, path)
            ? { ...file, folderPath: repathFolderPath(file.folderPath, path, newPath) }
            : file,
        );
      });
    }
    if (updates.parent !== undefined && updates.parent !== parentFolderPath(path)) {
      trackingStore.folderMoved({ libraryId: this.library.libraryId, via });
    }
    if (name !== undefined && name !== folderPathName(path)) {
      trackingStore.folderRenamed({ libraryId: this.library.libraryId });
    }
    return newPath;
  }

  /** The media in `path` or any folder under it. */
  private mediaIdsUnder(path: FolderPath): string[] {
    return this.allFiles
      .filter((file) => isSameOrDescendantFolderPath(this.folderOf(file), path))
      .map((file) => file._id);
  }

  /**
   * Deletes the folder and everything under it. The media any of it holds go with it, unless
   * `keepMedia`: then they are moved to the folder's parent first — the backend's own advice for
   * keeping them. If the delete fails after that move, the media are safe in the parent and the
   * folder is still there.
   */
  async deleteFolder(path: FolderPath, { keepMedia }: { keepMedia: boolean }) {
    const kept = keepMedia ? this.mediaIdsUnder(path) : [];
    if (kept.length > 0) await this.fileMedia(kept, parentFolderPath(path));

    // Read after the move: what is still under the folder is what the backend deletes.
    const doomed = this.mediaIdsUnder(path);
    const { deletedMediaCount } = await deleteLibraryFolder(this.library.libraryId, path);

    runInAction(() => {
      if (!this.data) return;
      const gone = new Set(doomed);
      this.data.files = this.data.files.filter((file) => !gone.has(file._id));
    });
    this.videoSelection.deselectItems(doomed);
    trackingStore.folderDeleted({
      libraryId: this.library.libraryId,
      mediaCount: deletedMediaCount,
      keptMediaCount: kept.length,
    });

    if (deletedMediaCount > 0) {
      this.library.update();
      this.scheduleCreditsRefresh('deleting a folder');
    }
    return { deletedMediaCount, keptMediaCount: kept.length };
  }

  /**
   * Moves media into `folderPath` and returns what moved and where from, so the move can be put
   * back with `undoMediaMove`. Media already there are left out of both.
   */
  async moveMedia(
    mediaIds: string[],
    folderPath: FolderPath,
    source: MediaMoveSource,
  ): Promise<MediaMove> {
    const move = await this.relocateMedia(mediaIds, folderPath);
    if (move.count > 0) {
      trackingStore.mediaMovedToFolder({
        libraryId: this.library.libraryId,
        count: move.count,
        via: source,
      });
    }
    return move;
  }

  /**
   * Puts media back in the folders `move` took them from: one call per folder, each all or none.
   * A folder that refuses (deleted meanwhile, a dropped request) doesn't stop the others; its media
   * are counted in `stranded`, with the first refusal as `error`.
   */
  async undoMediaMove(
    move: MediaMove,
  ): Promise<{ restored: number; stranded: number; error: unknown }> {
    let restored = 0;
    let stranded = 0;
    let error: unknown;
    for (const [origin, mediaIds] of move.from) {
      try {
        restored += (await this.relocateMedia(mediaIds, origin)).count;
      } catch (refusal) {
        stranded += mediaIds.length;
        error ??= refusal;
      }
    }
    if (restored > 0) {
      trackingStore.mediaMovedToFolder({
        libraryId: this.library.libraryId,
        count: restored,
        via: 'undo',
      });
    }
    return { restored, stranded, error };
  }

  /** `moveMedia` untracked. Media gone from the listing are left out, as are those already there. */
  private async relocateMedia(mediaIds: string[], folderPath: FolderPath): Promise<MediaMove> {
    const moving = new Set(mediaIds);
    const from = new Map<FolderPath, string[]>();
    for (const file of this.allFiles) {
      const origin = this.folderOf(file);
      if (!moving.has(file._id) || origin === folderPath) continue;
      const ids = from.get(origin);
      if (ids) ids.push(file._id);
      else from.set(origin, [file._id]);
    }
    const toMove = [...from.values()].flat();
    if (toMove.length > 0) await this.fileMedia(toMove, folderPath);
    return { count: toMove.length, from };
  }

  /** Files media in a folder and patches the listing to match, untracked. */
  private async fileMedia(mediaIds: string[], folderPath: FolderPath) {
    await moveMediaToFolder(this.library.libraryId, mediaIds, folderPath);
    runInAction(() => {
      if (!this.data) return;
      const moved = new Set(mediaIds);
      this.data.files = this.data.files.map((file) =>
        moved.has(file._id) ? { ...file, folderPath } : file,
      );
    });
    this.videoSelection.deselectItems(mediaIds);
  }

  async renameMedia(mediaId: string, newName: string) {
    await userStore.apiWithAuth(`/library/${this.library.libraryId}/media/${mediaId}`, {
      method: 'PATCH',
      body: {
        name: newName,
      },
    });
    trackingStore.mediaRenamed({ mediaId, libraryId: this.library.libraryId });

    if (this.data) {
      const index = this.data.files.findIndex((f) => f._id === mediaId);
      if (index !== -1) {
        this.data.files[index].metadata!.name = newName;
      }
    }
  }

  /** Drop a media row from the local list with no network call — used to clear a
   * failed upload immediately (works offline). The backend deletion is handled
   * separately/deferred (see UploadManager.enqueueMediaDeletion). */
  @action
  removeMediaLocally(mediaId: string) {
    if (!this.data) return;
    const index = this.data.files.findIndex((f) => f._id === mediaId);
    if (index !== -1) this.data.files.splice(index, 1);
  }

  @action
  async deleteMedia(mediaId: string) {
    await userStore.apiWithAuth(`/library/${this.library.libraryId}/media/${mediaId}`, {
      method: 'DELETE',
    });
    trackingStore.mediaDeleted({ mediaId, libraryId: this.library.libraryId });

    if (this.data) {
      const index = this.data.files.findIndex((f) => f._id === mediaId);
      if (index !== -1) {
        this.data.files.splice(index, 1);
      }
    }

    this.scheduleCreditsRefresh('deleting media');
  }

  @action
  async deleteBulkMedia(mediaIds: string[]) {
    await userStore.apiWithAuth(`/library/${this.library.libraryId}/media/delete/bulk`, {
      method: 'POST',
      body: {
        mediaIds,
      },
    });

    trackingStore.mediaBulkDeleted({
      mediaIds,
      libraryId: this.library.libraryId,
      count: mediaIds.length,
    });

    if (this.data) {
      // Use Set for O(n) lookup instead of O(n²)
      const idsSet = new Set(mediaIds);
      this.data.files = this.data.files.filter((file) => !idsSet.has(file._id));
    }

    this.scheduleCreditsRefresh('deleting bulk media');
  }

  /** Refreshes credits and storage once the backend has settled a deletion. */
  private scheduleCreditsRefresh(after: string) {
    setTimeout(async () => {
      try {
        await billingStore.refetchCreditsAfterOperation();
      } catch (error) {
        console.error(`Error refreshing credits after ${after}:`, error);
      }
    }, CREDITS_REFRESH_WAIT_TIME_AFTER_DELETION_MS);
  }

  @action
  async deleteFailedMedia() {
    const mediaIds = this.filesFailedOrAbandoned.map((media) => media._id);
    if (mediaIds.length === 0) {
      return true;
    }
    try {
      await this.deleteBulkMedia(mediaIds);
      return true;
    } catch (error) {
      console.error('Error deleting failed media:', error);
      return false;
    }
  }

  @action
  async retryMedia(libraryId: string, mediaId: string) {
    try {
      const media = await userStore.apiWithAuth<Media>(
        `/library/${libraryId}/media/${mediaId}/retry`,
        {
          method: 'POST',
        },
      );
      return media;
    } catch (error: any) {
      return {
        error: true,
        message: error.message,
      };
    }
  }

  @observable
  isRetryingAllMedia = false;

  @action
  setIsRetryingAllMedia(isRetrying: boolean) {
    this.isRetryingAllMedia = isRetrying;
  }

  @action
  retryAllFailedMedia = async () => {
    // Guard: nothing to retry
    if (this.filesFailed.length === 0) {
      return true;
    }

    this.setIsRetryingAllMedia(true);
    try {
      const result = await userStore.apiWithAuth<RetryAllMediaResponse>(
        `/library/${this.library.libraryId}/media/retry`,
        {
          method: 'POST',
        },
      );

      // Log response structure for verification during development
      if (process.env.NODE_ENV === 'development') {
        console.log('Retry all media response:', result);
      }

      // Parse the result to count successes and errors
      const successes = result.success ?? this.filesFailed.length;
      const errors = result.failed ?? 0;
      const errorMessages = result.errors ?? [];

      showToast('media-retry-all-result', { successes, errors, errorMessages });

      // Refresh folder data to update media statuses from "errored" to "queued"/"processing"
      // This ensures the UI immediately reflects the state change after bulk retry
      // Update whenever there are any successes, even if some items failed
      if (successes > 0) {
        await this.update();
      }

      return errors === 0;
    } catch (error: any) {
      console.error('Error retrying all media:', error);
      showToast('media-retry-failed', { error: error.message });
      return false;
    } finally {
      this.setIsRetryingAllMedia(false);
    }
  };

  @action
  async refreshMediaById(mediaId: string) {
    const media = await userStore.apiWithAuth<Media>(
      `/library/${this.library.libraryId}/media/${mediaId}`,
    );
    if (this.data) {
      const index = this.data.files.findIndex((f) => f._id === mediaId);
      if (index !== -1) {
        this.data.files[index] = media;
      }
    }
    return media;
  }
}

export const { Provider: LibraryFolderProvider, useStore: useLibraryFolder } = createContext(
  LibraryFolderStore,
  {
    dispose: (store) => store.deinit(),
  },
);
